import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Switch } from '@fluentui/react-components'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import {
  MessagesDataAssetsDataAsset,
  MessagesEntityTypesUpdateEntityType,
  MessagesTemplatesFileTemplate,
  MessagesTemplatesNewTemplate,
} from '@/dotnet-client'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { OverlayDrawerComponent } from '../OverlayDrawerComponent/OverlayDrawerComponent'

type DataAssetFormProps = {
  onSubmit: () => void
  edit?: boolean
  delete?: boolean
  selectedEntityType?: MessagesEntityTypesUpdateEntityType // Updated type
  selectedDataAsset?: MessagesDataAssetsDataAsset
  applicationId: number
  onDelete?: () => void // Optional delete callback
}

type TDataAssetForm = {
  id?: number
  name: string
  description: string
  key: string
  requireApprovals: boolean
}

export const DataAssetForm = (props: DataAssetFormProps) => {
  const { dmpClient, isDMPClientLoaded } = useDotnetClient()
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<TDataAssetForm>({
    defaultValues:
      (props.edit || props.delete) && props.selectedDataAsset
        ? ({
            ...props.selectedDataAsset,
          } as TDataAssetForm)
        : ({
            name: '',
            description: '',
            key: '',
            requireApprovals: true,
          } as TDataAssetForm),
  })

  useEffect(() => {
    if (isDMPClientLoaded) {
      reset()
    }
  }, [isDMPClientLoaded, reset])

  const onSubmit = (data: TDataAssetForm) => {
    if ((props.edit || props.delete) && props.selectedDataAsset) {
      const updatedTemplate: MessagesTemplatesFileTemplate = {
        ...props.selectedDataAsset,
        ...data,
      }

      dmpClient?.dataAssetApi
        .postDataAssetApiUpdateTemplate(updatedTemplate)
        .then(() => {
          toast.success('Data asset updated')
          props.onSubmit()
          reset()
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error updating data asset')
          props.onSubmit()
        })
    } else {
      const newTemplate: MessagesTemplatesNewTemplate = {
        entityTypeId: props.selectedEntityType!.id,
        applicationId: props.applicationId,
        ...data,
      }

      dmpClient?.dataAssetApi
        .putDataAssetApiAddDataAsset(newTemplate)
        .then(() => {
          toast.success('Data asset added')
          props.onSubmit()
          reset()
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error adding data asset')
          props.onSubmit()
        })
    }
  }

  const handleDelete = async () => {
    try {
      await dmpClient?.dataAssetApi.deleteDataAssetApiDeleteTemplate(props.selectedDataAsset)
      toast.success('Data asset successfully deleted')
      props.onDelete!() // Ensure this does not cause the parent component to unmount immediately
      setIsDeleteConfirmOpen(false) // Close the drawer after toast displays
    } catch (err) {
      console.error('Error deleting data asset:', err)
      toast.error('Error deleting data asset')
    }
  }

  return (
    <div className="form-wrapper">
      <div>
        {/* Name Field */}
        <Field
          label="Name"
          required
          validationState={errors.name ? 'error' : 'none'}
          validationMessage={errors.name?.message}
        >
          <Input
            {...register('name', {
              required: 'Name is required',
              disabled: props.delete,
              maxLength: 100,
              setValueAs: (value) => value ?? undefined,
            })}
          />
        </Field>

        {/* Description Field */}
        <Field
          label="Description"
          required
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.message}
        >
          <Input
            {...register('description', {
              required: 'Description is required',
              disabled: props.delete,
              maxLength: 200,
              setValueAs: (value) => value ?? undefined,
            })}
          />
        </Field>

        <Field
          label="Key"
          required
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.message}
        >
          <Input
            {...register('key', {
              disabled: props.delete,
              maxLength: 200,
              setValueAs: (value) => value ?? undefined,
            })}
          />
        </Field>

        <Field
          label="Require Approvals?"
          required
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.message}
        >
          <Switch {...register('requireApprovals', { disabled: props.delete })} />
        </Field>
      </div>

      {/* Submit Button */}
      <div className="form-action-container">
        {props.delete && props.onDelete && <Button text="Delete" onClick={() => setIsDeleteConfirmOpen(true)} />}
        {!props.delete && <Button text="Submit" onClick={handleSubmit(onSubmit)} />}
      </div>

      {/* Confirmation Modal for Delete */}
      <OverlayDrawerComponent
        open={isDeleteConfirmOpen}
        setOpen={setIsDeleteConfirmOpen}
        title="Confirm Delete"
        content={
          <div>
            <p>Are you sure you want to delete this data asset?</p>
            <Button text="Confirm" onClick={handleDelete} />
          </div>
        }
      />
    </div>
  )
}
